import { useCallback, useEffect, useRef } from 'react';

export type Callback<T extends any[]> = (...args: T) => unknown;

export type UseQueuedCallbackOptions = {
  /**
   * When false, any time the queuedCallback is called the original callback
   * will NOT be called and all args will instead be added to the eventQueue
   * to be handled later once the callback is ready.
   * When true, the queuedCallback will behave exactly as the original callback;
   * nothing will be added to the queue and all args will be forwarded to the
   * original callback.
   */
  isCallbackReady?: boolean;
  /**
   * When true, the original callback will be run on all events in the queue at
   * the time when the callback is ready (isCallbackReady is set to true).
   * When false, the original callback will not be run on the queue. It will
   * be up to you to use the eventQueue as desired.
   */
  autoRunQueueWhenReady?: boolean;
};

/**
 * Utility hook that returns an augmented callback that will queue all calls
 * until the callback is marked as ready. Queued calls can be automatically run
 * once the callback is ready, or can be handled manually by the consumer.
 */
const useQueuedCallback = <T extends any[]>(
  callback: Callback<T>,
  options: UseQueuedCallbackOptions | undefined | void,
) => {
  const { isCallbackReady = true, autoRunQueueWhenReady = false } =
    options || {};

  const eventQueue = useRef<T[]>([]);

  // Use this ref to maintain an up-to-date reference to the isCallbackReady value
  // that won't be closed over by the useCallback closure.
  const isCallbackReadyRef = useRef(isCallbackReady);
  // We update it here so that it always has the most recent isCallbackReady value.
  isCallbackReadyRef.current = isCallbackReady;

  useEffect(() => {
    if (isCallbackReady && autoRunQueueWhenReady && eventQueue.current.length) {
      eventQueue.current?.forEach((args) => callback(...args));
      eventQueue.current = [];
    }
  }, [autoRunQueueWhenReady, callback, isCallbackReady]);

  const queuedCallback = useCallback(
    (...args: T) => {
      if (!isCallbackReadyRef.current) {
        eventQueue.current.push(args);
        return;
      }

      callback(...args);
    },
    [callback],
  );

  return [queuedCallback, eventQueue.current] as const;
};

export default useQueuedCallback;
